import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";
import { formatDateToDDMMYYYY } from "../pages/const";
import PDFHeader from "./PDFHeader";

/** Landscape A4 with metal + amount columns. */
const ROWS_PER_PAGE = 22;

const styles = StyleSheet.create({
  page: {
    paddingTop: 14,
    paddingBottom: 18,
    paddingHorizontal: 16,
    fontFamily: "Times-Roman",
  },
  customerStrip: {
    marginTop: 4,
    marginBottom: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#8c1d1a",
    backgroundColor: "#faf6f0",
  },
  customerStripItem: {
    marginBottom: 2,
  },
  customerLabel: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#8c1d1a",
  },
  customerValue: {
    fontSize: 9,
    color: "#000",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#8c1d1a",
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#8c1d1a",
    borderBottomStyle: "solid",
    width: "100%",
  },
  tableHeader: {
    backgroundColor: "#f0e6d2",
    fontWeight: "bold",
  },
  tableTotalRow: {
    backgroundColor: "#f0e6d2",
    fontWeight: "bold",
  },
  cellDate: {
    fontSize: 7,
    padding: 2,
    width: "7%",
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
  },
  cellType: {
    fontSize: 7,
    padding: 2,
    width: "12%",
    textAlign: "left",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
  },
  cellNum: {
    fontSize: 7,
    padding: 2,
    width: "7%",
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
  },
  cellRemarks: {
    fontSize: 7,
    padding: 2,
    width: "11%",
    textAlign: "left",
    borderRightWidth: 0,
  },
  headerMain: {
    fontSize: 8,
    fontWeight: "bold",
    padding: 2,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
  },
  headerSub: {
    fontSize: 7,
    fontWeight: "bold",
    padding: 2,
    width: "7%",
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#8c1d1a",
    borderRightStyle: "solid",
    backgroundColor: "#f0e6d2",
  },
  headerSubLast: {
    fontSize: 7,
    fontWeight: "bold",
    padding: 2,
    width: "11%",
    textAlign: "center",
    borderRightWidth: 0,
    backgroundColor: "#f0e6d2",
  },
  openingBalanceRow: {
    backgroundColor: "#e3f2fd",
  },
  footer: {
    marginTop: 14,
    fontSize: 8,
    textAlign: "center",
    color: "#555",
  },
});

const CREDIT_BG = "#e8f5ea";
const DEBIT_BG = "#fce8ea";
const BALANCE_BG = "#e8f4f7";

function getFormattedGeneratedOn() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
}

const formatDisplayDate = (dateString) => {
  if (!dateString || dateString === "-") return "-";
  try {
    const date = new Date(dateString);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB");
    }
    return formatDateToDDMMYYYY(String(dateString).split(" ")[0]) || dateString;
  } catch {
    return dateString;
  }
};

function chunkRows(rows, size) {
  if (!rows.length) return [[]];
  const pages = [];
  for (let i = 0; i < rows.length; i += size) {
    pages.push(rows.slice(i, i + size));
  }
  return pages;
}

function balanceColor(value, isOpening) {
  if (isOpening) return "#000";
  const n = parseFloat(value || 0);
  if (n < 0) return "#dc3545";
  if (n > 0) return "#28a745";
  return "#000";
}

const TableHeaderRows = () => (
  <>
    <View style={[styles.tableRow, styles.tableHeader]}>
      <Text style={[styles.cellDate, { backgroundColor: "#f0e6d2" }]}>DATE</Text>
      <Text style={[styles.cellType, { backgroundColor: "#f0e6d2" }]}>TYPE</Text>
      <Text style={[styles.headerMain, { backgroundColor: CREDIT_BG, width: "21%" }]}>
        CREDITED
      </Text>
      <Text style={[styles.headerMain, { backgroundColor: DEBIT_BG, width: "21%" }]}>
        DEBITED
      </Text>
      <Text style={[styles.headerMain, { backgroundColor: BALANCE_BG, width: "21%" }]}>
        BALANCE
      </Text>
      <Text style={[styles.headerSubLast, { backgroundColor: "#f0e6d2" }]}>
        REMARKS
      </Text>
    </View>
    <View style={[styles.tableRow, styles.tableHeader]}>
      <Text style={[styles.cellDate, { backgroundColor: "#f0e6d2" }]}></Text>
      <Text style={[styles.cellType, { backgroundColor: "#f0e6d2" }]}></Text>
      <Text style={[styles.headerSub, { backgroundColor: CREDIT_BG }]}>Gold</Text>
      <Text style={[styles.headerSub, { backgroundColor: CREDIT_BG }]}>Silver</Text>
      <Text style={[styles.headerSub, { backgroundColor: CREDIT_BG }]}>Amt</Text>
      <Text style={[styles.headerSub, { backgroundColor: DEBIT_BG }]}>Gold</Text>
      <Text style={[styles.headerSub, { backgroundColor: DEBIT_BG }]}>Silver</Text>
      <Text style={[styles.headerSub, { backgroundColor: DEBIT_BG }]}>Amt</Text>
      <Text style={[styles.headerSub, { backgroundColor: BALANCE_BG }]}>Gold</Text>
      <Text style={[styles.headerSub, { backgroundColor: BALANCE_BG }]}>Silver</Text>
      <Text style={[styles.headerSub, { backgroundColor: BALANCE_BG }]}>Amt</Text>
      <Text style={styles.headerSubLast}></Text>
    </View>
  </>
);

const CustomerDetailsPDF = ({
  ledgerData = [],
  customerInfo = {},
  fromDate = "",
  toDate = "",
  entireReport = false,
}) => {
  const phone = customerInfo?.mobile || customerInfo?.phone || "-";
  const rawName = customerInfo?.name || customerInfo?.fullName || "-";
  const name =
    phone && phone !== "-"
      ? String(rawName)
          .replace(new RegExp(`\\s*\\(${phone}\\)\\s*$`), "")
          .trim() || rawName
      : rawName;

  const totals = ledgerData.reduce(
    (acc, row) => {
      acc.goldCredit += parseFloat(row.credit) || 0;
      acc.goldDebit += parseFloat(row.debit) || 0;
      acc.silverCredit += parseFloat(row.silver_credit) || 0;
      acc.silverDebit += parseFloat(row.silver_debit) || 0;
      acc.amountCredit += parseFloat(row.amount_credit) || 0;
      acc.amountDebit += parseFloat(row.amount_debit) || 0;
      return acc;
    },
    {
      goldCredit: 0,
      goldDebit: 0,
      silverCredit: 0,
      silverDebit: 0,
      amountCredit: 0,
      amountDebit: 0,
    },
  );

  const last = ledgerData.length > 0 ? ledgerData[ledgerData.length - 1] : null;
  const finalGoldBalance = last ? parseFloat(last.balance || 0) : 0;
  const finalSilverBalance = last ? parseFloat(last.silver_balance || 0) : 0;
  const finalAmountBalance = last ? parseFloat(last.amount_balance || 0) : 0;

  const pages = chunkRows(ledgerData, ROWS_PER_PAGE);
  const totalPages = pages.length;

  return (
    <Document>
      {pages.map((pageRows, pageIndex) => {
        const isFirst = pageIndex === 0;
        const isLast = pageIndex === totalPages - 1;

        return (
          <Page
            key={pageIndex}
            size="A4"
            style={styles.page}
            orientation="landscape"
          >
            <PDFHeader
              title="Customer Ledger Report"
              titleStyle={{ fontSize: 12, paddingVertical: 2 }}
            />

            {isFirst ? (
              <View style={styles.customerStrip}>
                <Text style={[styles.customerValue, styles.customerStripItem]}>
                  <Text style={styles.customerLabel}>Name: </Text>
                  {name}
                </Text>
                <Text style={[styles.customerValue, styles.customerStripItem]}>
                  <Text style={styles.customerLabel}>Phone: </Text>
                  {phone}
                </Text>
                {entireReport || fromDate || toDate ? (
                  <Text style={[styles.customerValue, styles.customerStripItem]}>
                    <Text style={styles.customerLabel}>Period: </Text>
                    {entireReport
                      ? "Entire Report"
                      : `${formatDateToDDMMYYYY(fromDate) || "-"} to ${
                          formatDateToDDMMYYYY(toDate) || "-"
                        }`}
                  </Text>
                ) : null}
              </View>
            ) : null}

            <View style={styles.table}>
              <TableHeaderRows />
              {pageRows.map((row, idx) => (
                <View
                  key={`${pageIndex}-${idx}`}
                  style={[
                    styles.tableRow,
                    row.isOpening && styles.openingBalanceRow,
                  ]}
                >
                  <Text style={styles.cellDate}>
                    {row.isOpening
                      ? "-"
                      : formatDisplayDate(row.date)}
                  </Text>
                  <Text style={styles.cellType}>
                    {row.type || (row.isOpening ? "Opening Balance" : "-")}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: CREDIT_BG }]}>
                    {row.credit || "-"}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: CREDIT_BG }]}>
                    {row.silver_credit || "-"}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: CREDIT_BG }]}>
                    {row.amount_credit || "-"}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: DEBIT_BG }]}>
                    {row.debit || "-"}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: DEBIT_BG }]}>
                    {row.silver_debit || "-"}
                  </Text>
                  <Text style={[styles.cellNum, { backgroundColor: DEBIT_BG }]}>
                    {row.amount_debit || "-"}
                  </Text>
                  <Text
                    style={[
                      styles.cellNum,
                      {
                        backgroundColor: BALANCE_BG,
                        color: balanceColor(row.balance, row.isOpening),
                      },
                    ]}
                  >
                    {row.balance || "-"}
                  </Text>
                  <Text
                    style={[
                      styles.cellNum,
                      {
                        backgroundColor: BALANCE_BG,
                        color: balanceColor(row.silver_balance, row.isOpening),
                      },
                    ]}
                  >
                    {row.silver_balance || "-"}
                  </Text>
                  <Text
                    style={[
                      styles.cellNum,
                      {
                        backgroundColor: BALANCE_BG,
                        color: balanceColor(row.amount_balance, row.isOpening),
                      },
                    ]}
                  >
                    {row.amount_balance || "-"}
                  </Text>
                  <Text style={styles.cellRemarks}>{row.remarks || ""}</Text>
                </View>
              ))}

              {isLast ? (
                <View style={[styles.tableRow, styles.tableTotalRow]}>
                  <Text style={styles.cellDate}></Text>
                  <Text style={[styles.cellType, { fontWeight: "bold" }]}>
                    TOTAL
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: CREDIT_BG }]}>
                    {totals.goldCredit ? totals.goldCredit.toFixed(3) : "-"}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: CREDIT_BG }]}>
                    {totals.silverCredit ? totals.silverCredit.toFixed(3) : "-"}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: CREDIT_BG }]}>
                    {totals.amountCredit.toFixed(2)}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: DEBIT_BG }]}>
                    {totals.goldDebit ? totals.goldDebit.toFixed(3) : "-"}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: DEBIT_BG }]}>
                    {totals.silverDebit ? totals.silverDebit.toFixed(3) : "-"}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: DEBIT_BG }]}>
                    {totals.amountDebit.toFixed(2)}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: BALANCE_BG }]}>
                    {finalGoldBalance.toFixed(3)}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: BALANCE_BG }]}>
                    {finalSilverBalance.toFixed(3)}
                  </Text>
                  <Text style={[styles.cellNum, { fontWeight: "bold", backgroundColor: BALANCE_BG }]}>
                    {finalAmountBalance.toFixed(2)}
                  </Text>
                  <Text style={styles.cellRemarks}></Text>
                </View>
              ) : null}
            </View>

            {isLast ? (
              <View style={styles.footer}>
                <Text>
                  Generated on: {getFormattedGeneratedOn()}
                  {totalPages > 1
                    ? `  |  Page ${pageIndex + 1} of ${totalPages}`
                    : ""}
                </Text>
              </View>
            ) : (
              <View style={styles.footer}>
                <Text>
                  Page {pageIndex + 1} of {totalPages}
                </Text>
              </View>
            )}
          </Page>
        );
      })}
    </Document>
  );
};

export default CustomerDetailsPDF;
